'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { InfoCard } from '@/ui/cards/info-card/InfoCard'
import type { ProductPackaging } from '@gql-generated/graphql'
import { Unit } from '@gql-generated/graphql'
import type React from 'react'
import { getNextTierPriceForQuantity } from '../../services/product.filtering'
import type { ProductTierPriceProps } from './ProductTiersPrices.client'

type ProductTiersPricesInfoCardProps = {
  packaging?: ProductPackaging | null
  priceQuantity?: number | null
  tiersPrices?: ProductTierPriceProps[] | null
  selectedQuantity?: number
  isLoading?: boolean
}

export function ProductTiersPricesInfoCard({
  packaging,
  priceQuantity,
  tiersPrices,
  selectedQuantity = 0,
  isLoading = false,
}: ProductTiersPricesInfoCardProps): React.ReactNode {
  const { t, Trans } = useTranslation(['generic', 'productTiersPrices'])
  const opacityClassName = isLoading && 'opacity-30'

  if (!tiersPrices?.length || !packaging) {
    return null
  }

  const nextTier = getNextTierPriceForQuantity(tiersPrices, selectedQuantity)

  if (!nextTier) return null

  const gap = Math.max(nextTier.quantity - selectedQuantity, 0)

  if (gap <= 0) return null

  const unit = packaging.unit || Unit.P
  const unitQuantity = priceQuantity ?? packaging.quantity ?? 1
  const gapUnitText = t(`generic:units.${unit}`, { count: gap })
  const unitLabel = t(`generic:units.${unit}`, { count: unitQuantity })
  const discountedUnitPrice = formatPrice(nextTier.exclTax)

  return (
    <InfoCard
      variant="error"
      className={twMerge(opacityClassName)}
      content={
        <Trans
          i18nKey="productTiersPrices:quantitySale"
          components={{
            gap: <strong />,
            discountedUnitPriceAndUnit: <span className="font-bold text-strawberry-red-600" />,
          }}
          values={{
            gap,
            gapUnitText,
            discountedUnitPrice,
            unitQuantity,
            unit: unitLabel,
          }}
        />
      }
    />
  )
}
